/**
 * Planos de saúde sobre o PostgREST — trilha T5a.
 *
 * A contagem de pacientes sai de `pacientes.convenio_id`, lida em páginas: um
 * `count` por plano seria um pedido por linha da tabela.
 */

import { supabase } from '@/supabase';
import { atual } from '@/session/sessao';
import { lerTudo } from './paginacaoNoServidor';
import { normalizarPlano, problemaDoPlano } from './planosDeSaude';
import type { DadosDoPlanoDeSaude, PlanoDeSaude, TipoDePlano } from './planosDeSaude';
import type { PortaDeDados } from './porta';

type MetodosDosPlanos = Pick<PortaDeDados, 'planosDeSaude' | 'salvarPlanoDeSaude'>;

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

function clinicaId(): string {
    const id = atual()?.clinicaId;
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

function erro(mensagem: string, e: { message: string; code?: string } | null): void {
    if (!e) return;
    if (e.code === '42501') throw new Error('Só quem administra a clínica cadastra planos de saúde.');
    if (e.code === '23505') throw new Error('Já existe um plano com esse nome.');
    if (e.code === '23514') throw new Error('Algum campo do plano está fora do formato: confira o registro na ANS, o repasse e a cor.');
    throw new Error(`${mensagem}: ${e.message}`);
}

interface LinhaDoPlano {
    id: string;
    nome: string;
    operadora: string | null;
    tipo: string;
    registro_ans: string | null;
    carencia: string | null;
    tabela_de_precos: string | null;
    repasse_percentual: number | string | null;
    prazo_recebimento: string | null;
    cor: string;
    observacoes: string | null;
    ativo: boolean;
}

export const planosNoSupabase: MetodosDosPlanos = {
    async planosDeSaude(): Promise<PlanoDeSaude[]> {
        const [{ data, error }, fichas] = await Promise.all([
            cliente()
                .from('convenios')
                .select('id, nome, operadora, tipo, registro_ans, carencia, tabela_de_precos, repasse_percentual, prazo_recebimento, cor, observacoes, ativo')
                .order('nome'),
            lerTudo<Record<string, unknown>>((de, ate) => cliente()
                .from('pacientes').select('convenio_id').not('convenio_id', 'is', null).order('contato_id').range(de, ate)),
        ]);
        erro('Falha ao ler os planos de saúde', error);

        const contagem = new Map<string, number>();
        for (const f of fichas) {
            const id = typeof f.convenio_id === 'string' ? f.convenio_id : '';
            if (id) contagem.set(id, (contagem.get(id) ?? 0) + 1);
        }

        return ((data ?? []) as unknown as LinhaDoPlano[]).map((l) => ({
            id: l.id,
            nome: l.nome,
            tipo: l.tipo as TipoDePlano,
            cor: l.cor,
            ativo: l.ativo,
            ...(l.operadora ? { operadora: l.operadora } : {}),
            ...(l.registro_ans ? { registroAns: l.registro_ans } : {}),
            ...(l.carencia ? { carencia: l.carencia } : {}),
            ...(l.tabela_de_precos ? { tabelaDePrecos: l.tabela_de_precos } : {}),
            ...(l.repasse_percentual !== null ? { repassePercentual: Number(l.repasse_percentual) } : {}),
            ...(l.prazo_recebimento ? { prazoRecebimento: l.prazo_recebimento } : {}),
            ...(l.observacoes ? { observacoes: l.observacoes } : {}),
            pacientes: contagem.get(l.id) ?? 0,
        }));
    },

    async salvarPlanoDeSaude(id: string | null, dados: DadosDoPlanoDeSaude): Promise<{ id: string }> {
        const problema = problemaDoPlano(dados);
        if (problema) throw new Error(problema);
        const d = normalizarPlano(dados);
        const linha = {
            nome: d.nome, tipo: d.tipo, cor: d.cor, ativo: d.ativo,
            operadora: d.operadora ?? null,
            registro_ans: d.registroAns ?? null,
            carencia: d.carencia ?? null,
            tabela_de_precos: d.tabelaDePrecos ?? null,
            repasse_percentual: d.repassePercentual ?? null,
            prazo_recebimento: d.prazoRecebimento ?? null,
            observacoes: d.observacoes ?? null,
        };

        if (!id) {
            const { data, error } = await cliente().from('convenios').insert({ clinica_id: clinicaId(), ...linha }).select('id').single();
            erro('Falha ao cadastrar o plano', error);
            return { id: (data as unknown as { id: string }).id };
        }

        /* Update barrado pela RLS não dá erro: volta sem linha. */
        const { data, error } = await cliente().from('convenios').update(linha).eq('id', id).select('id');
        erro('Falha ao salvar o plano', error);
        if ((data ?? []).length === 0) throw new Error('Plano não encontrado, ou sem permissão para alterá-lo.');
        return { id };
    },
};
